import scriptLoadService from '../../react/navigation/Service/script-load-service';
import { installTractiveNoopStub } from '../../react/navigation/Service/tractive-noop-stub';
import { startAmplitudeMonitoring } from '../../react/navigation/Service/amplitude-monitoring';
import { startAmplitudeRequestBlocking } from '../../react/navigation/Service/amplitude-request-blocking';
import { logAmplitudeRequest } from '../../react/navigation/Service/metrics-service';

const loaderService = scriptLoadService();

const loadTractiveScript = () => {
  const url = '/tractive/scripts/tractive.min.js';

  loaderService
    .loadScript(url, 'internalTractiveScript')
    .catch((error) =>
      console.error(`Global Wrapper failed to load ${url}:`, error),
    );
};

// One value per outcome, so log entries can be filtered on a single field.
export const AMPLITUDE_REQUEST_CAUSE = {
  SENT: 'sentToAmplitude',
  BLOCKED_BY_TRACTIVE_NOOP: 'blockedByTractiveNoop',
  BLOCKED_BY_REQUEST_BLOCKING: 'blockedByRequestBlocking',
};

// One report per page URL and cause, not one per Amplitude call.
// Keyed by URL so single-page apps still get a report after navigating.
const reportedAmplitudeRequests = new Set();

const createAmplitudeRequestReporter =
  ({ pageId, context }, cause) =>
  () => {
    // eslint-disable-next-line angular/window-service
    const url = window.location.href;
    const reportKey = `${url}|${cause}`;
    if (reportedAmplitudeRequests.has(reportKey)) {
      return;
    }
    reportedAmplitudeRequests.add(reportKey);
    logAmplitudeRequest({
      url,
      pageId,
      context,
      cause,
      wasAmplitudeSent: cause === AMPLITUDE_REQUEST_CAUSE.SENT,
    });
  };

// Called from initialization-service once the bundle resolves, where the per-brand flags will come from.
// When the bundle fails no flags are passed and the real script loads, same as before this gate existed.
// Runs once per page: initialize() runs on every ReactContainer mount, and repeating this would add
// another script tag, wrap fetch/XHR/sendBeacon again and start another observer.
let isTractiveInitialized = false;

// Monitoring and blocking are optional extras; a failure in either must not stop Tractive from loading.
const runSafely = (description, setUp) => {
  try {
    setUp();
  } catch (error) {
    console.error(`Global Wrapper failed to start ${description}:`, error);
  }
};

export const tractiveInit = (amplitudeFlags, pageInfo) => {
  if (isTractiveInitialized) {
    return;
  }
  isTractiveInitialized = true;

  // Request blocking is an extra layer on top of BlockAmplitude, never on its own.
  if (
    amplitudeFlags?.isTractiveNoopEnabled &&
    amplitudeFlags?.isRequestBlockingEnabled
  ) {
    // Blocked requests never reach the network, so monitoring can't see them; they are reported here.
    runSafely('Amplitude request blocking', () =>
      startAmplitudeRequestBlocking(
        createAmplitudeRequestReporter(
          pageInfo,
          AMPLITUDE_REQUEST_CAUSE.BLOCKED_BY_REQUEST_BLOCKING,
        ),
      ),
    );
  }

  if (amplitudeFlags?.isMonitoringEnabled) {
    runSafely('Amplitude monitoring', () =>
      startAmplitudeMonitoring(
        createAmplitudeRequestReporter(pageInfo, AMPLITUDE_REQUEST_CAUSE.SENT),
      ),
    );
  }

  loaderService.runOnLoad(function injectTractiveOnLoad() {
    if (amplitudeFlags?.isTractiveNoopEnabled) {
      installTractiveNoopStub(
        createAmplitudeRequestReporter(
          pageInfo,
          AMPLITUDE_REQUEST_CAUSE.BLOCKED_BY_TRACTIVE_NOOP,
        ),
      );
      return;
    }
    loadTractiveScript();
  });
};
